import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, X, Plus, Truck, Package, DollarSign, Tag, FileText, MapPin, ArrowLeft, Loader2, Scale } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { detectCountryName } from '../utils/locationUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WEIGHT_CLASSES = [
  { id: 'small', label: 'Small (up to 500g)', kg: 0.5, length: 20, width: 15, height: 10, description: 'Letters, small items, accessories' },
  { id: 'medium', label: 'Medium (500g – 1kg)', kg: 1, length: 30, width: 20, height: 15, description: 'Clothing, books, small toys' },
  { id: 'large', label: 'Large (1 – 3kg)', kg: 3, length: 40, width: 30, height: 20, description: 'Multiple items, shoes, larger toys' },
  { id: 'xlarge', label: 'Extra Large (3 – 5kg)', kg: 5, length: 50, width: 40, height: 25, description: 'Heavy items, electronics' },
  { id: 'heavy', label: 'Heavy (5 – 10kg)', kg: 10, length: 60, width: 40, height: 30, description: 'Large electronics, furniture pieces' },
  { id: 'bulky', label: 'Bulky (10 – 22kg)', kg: 22, length: 80, width: 60, height: 40, description: 'Large/heavy items, may require courier' },
];

const EditListingPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { categories, products, user, isAuthenticated, updateProduct } = useApp();
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    originalPrice: '',
    category: '',
    condition: '',
    location: '',
    suburb: '',
    state: '',
    overseas: false,
    country: '',
    shippingAvailable: false,
    weightClass: '',
    pickupAvailable: true,
    listingCurrency: 'AUD'
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const loadedRef = useRef(false);

  const conditions = ['New', 'Like New', 'Excellent', 'Very Good', 'Good', 'Fair'];

  // Load existing product data (from context, falling back to API so any listing is editable)
  useEffect(() => {
    if (loadedRef.current || !productId || !user?.id) return;

    const apply = (product) => {
      const sellerId = product.seller_id || product.sellerId;
      if (sellerId !== user?.id) {
        toast.error("You can only edit your own listings");
        navigate('/my-listings');
        return;
      }
      loadedRef.current = true;
      const existingCountry = product.country && product.country !== 'Australia' ? product.country : '';
      const suburbLooksLikeCountry = detectCountryName(product.suburb);
      setFormData({
        title: product.title || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        originalPrice: product.originalPrice?.toString() || product.original_price?.toString() || '',
        category: product.category || '',
        condition: product.condition || '',
        location: product.location || '',
        suburb: suburbLooksLikeCountry && suburbLooksLikeCountry !== 'Australia' ? '' : (product.suburb || ''),
        state: product.state || (product.location || '').split(',').pop().trim().toUpperCase().match(/^(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)$/)?.[0] || '',
        overseas: !!existingCountry || (!!suburbLooksLikeCountry && suburbLooksLikeCountry !== 'Australia'),
        country: existingCountry || (suburbLooksLikeCountry && suburbLooksLikeCountry !== 'Australia' ? suburbLooksLikeCountry : ''),
        shippingAvailable: product.shipping_available || product.shippingAvailable || false,
        weightClass: product.weight_class || product.weightClass || '',
        pickupAvailable: product.pickup_available !== false,
        listingCurrency: product.listing_currency || 'AUD'
      });
      setImages(product.images || []);
      setInitialLoading(false);
    };

    const fromContext = products.find(p => p.id === productId);

    (async () => {
      // Always load the FULL product from the API — the context list is a slim
      // projection (no description, thumbnail-only images) and would wipe data on save
      try {
        const res = await fetch(`${BACKEND_URL}/api/products/${productId}?track_view=false`);
        if (res.ok) {
          apply(await res.json());
          return;
        }
        if (res.status === 404 && fromContext) {
          apply(fromContext);
          return;
        }
        toast.error("Listing not found");
        navigate('/my-listings');
      } catch {
        if (fromContext) {
          apply(fromContext);
          return;
        }
        toast.error("Couldn't load your listing. Please check your connection and try again.");
        setInitialLoading(false);
      }
    })();
  }, [productId, products, user, navigate]);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const selectedWeightClass = WEIGHT_CLASSES.find(w => w.id === formData.weightClass);

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1200;
          let { width, height } = img;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => reject(new Error('unsupported-format'));
        img.src = ev.target.result;
      };
      reader.onerror = () => reject(new Error('read-failed'));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        setImages(prev => [...prev, compressed]);
      } catch {
        toast.error(`"${file.name}" isn't a supported photo format. Please use JPG, PNG or take the photo again.`);
      }
    }
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price || !formData.category || !formData.condition) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setLoading(true);

    const suburbCountry = detectCountryName(formData.suburb);
    if (!formData.overseas && suburbCountry && suburbCountry !== 'Australia') {
      toast.error(`"${formData.suburb}" looks like a country, not an Australian suburb. Tick "I'm selling from outside Australia" in the Location section.`);
      setLoading(false);
      return;
    }

    try {
      const updates = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        original_price: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
        category: formData.category,
        condition: formData.condition,
        location: formData.overseas
          ? [formData.suburb.trim(), detectCountryName(formData.country) || formData.country.trim()].filter(Boolean).join(', ')
          : (formData.suburb && formData.state ? `${formData.suburb}, ${formData.state}` : formData.location),
        suburb: formData.suburb,
        state: formData.overseas ? '' : formData.state,
        country: formData.overseas ? (detectCountryName(formData.country) || formData.country.trim()) : 'Australia',
        images: images,
        shipping_available: formData.shippingAvailable,
        weight_class: formData.weightClass,
        weight: selectedWeightClass?.kg || 1,
        parcel_length: selectedWeightClass?.length || 30,
        parcel_width: selectedWeightClass?.width || 20,
        parcel_height: selectedWeightClass?.height || 10,
        pickup_available: formData.pickupAvailable,
        listing_currency: formData.listingCurrency,
        updated_at: new Date().toISOString()
      };

      // Call API to update product
      const response = await fetch(`${BACKEND_URL}/api/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        // Also update local state
        if (updateProduct) {
          updateProduct(productId, updates);
        }
        toast.success('Listing updated successfully!');
        navigate('/my-listings');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update listing');
      }
    } catch (error) {
      console.error('Error updating listing:', error);
      toast.error('Failed to update listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-minted-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold">Edit Listing</h1>
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Images */}
        <div>
          <Label className="text-base font-medium flex items-center gap-2">
            <Camera className="w-5 h-5 text-minted-600" />
            Photos ({images.length}/5)
          </Label>
          <p className="text-sm text-gray-500 mb-3">First image will be the cover photo</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {images.map((img, index) => (
              <div key={index} className="relative flex-shrink-0">
                <img
                  src={img}
                  alt={`Product ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
                {index === 0 && (
                  <span className="absolute bottom-1 left-1 text-xs bg-minted-600 text-white px-1 rounded">
                    Cover
                  </span>
                )}
              </div>
            ))}
            {images.length < 5 && (
              <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-minted-500 transition-colors flex-shrink-0">
                <Plus className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-400">Add</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  multiple
                />
              </label>
            )}
          </div>
        </div>

        {/* Title */}
        <div>
          <Label htmlFor="title" className="text-base font-medium flex items-center gap-2">
            <Tag className="w-5 h-5 text-minted-600" />
            Title *
          </Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="What are you selling?"
            className="mt-2"
            required
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description" className="text-base font-medium flex items-center gap-2">
            <FileText className="w-5 h-5 text-minted-600" />
            Description
          </Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your item, include details like brand, size, condition..."
            className="mt-2 min-h-[100px]"
          />
        </div>

        {/* Price */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price" className="text-base font-medium flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-minted-600" />
              Price *
            </Label>
            <div className="flex mt-2 gap-1">
              <select
                name="listingCurrency"
                value={formData.listingCurrency}
                onChange={handleChange}
                className="h-10 px-2 rounded-md border border-input bg-background text-sm w-20 flex-shrink-0"
                data-testid="edit-listing-currency-select"
              >
                <option value="AUD">A$</option>
                <option value="GBP">£</option>
                <option value="USD">US$</option>
                <option value="EUR">€</option>
                <option value="NZD">NZ$</option>
                <option value="CAD">CA$</option>
              </select>
              <Input
                id="price"
                name="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={handleChange}
                placeholder="0"
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="originalPrice" className="text-base font-medium text-gray-500">
              Original Price
            </Label>
            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{({ GBP: '£', USD: '$', EUR: '€' }[formData.listingCurrency]) || '$'}</span>
              <Input
                id="originalPrice"
                name="originalPrice"
                type="number"
                step="0.01"
                value={formData.originalPrice}
                onChange={handleChange}
                placeholder="Optional"
                className="pl-7"
              />
            </div>
          </div>
        </div>

        {/* Category */}
        <div>
          <Label className="text-base font-medium">Category *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Condition */}
        <div>
          <Label className="text-base font-medium">Condition *</Label>
          <Select
            value={formData.condition}
            onValueChange={(value) => setFormData({ ...formData, condition: value })}
          >
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Select condition" />
            </SelectTrigger>
            <SelectContent>
              {conditions.map(cond => (
                <SelectItem key={cond} value={cond}>{cond}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div>
          <Label className="text-base font-medium flex items-center gap-2">
            <MapPin className="w-5 h-5 text-minted-600" />
            Location
          </Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Input
              id="suburb"
              name="suburb"
              value={formData.suburb}
              onChange={handleChange}
              placeholder={formData.overseas ? 'City (e.g., Los Angeles)' : 'Suburb (e.g., Penrith)'}
              data-testid="edit-suburb-input"
            />
            {formData.overseas ? (
              <Input
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Country (e.g., USA)"
                data-testid="edit-country-input"
              />
            ) : (
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="edit-state-select"
              >
                <option value="">State</option>
                {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.overseas}
              onChange={(e) => setFormData(prev => ({ ...prev, overseas: e.target.checked, state: '' }))}
              data-testid="edit-overseas-checkbox"
              className="rounded border-gray-300"
            />
            I'm selling from outside Australia
          </label>
          {!formData.suburb && formData.location && (
            <p className="text-xs text-gray-400 mt-1">Currently showing: "{formData.location}" — add a suburb & state to help local buyers find you</p>
          )}
        </div>

        {/* Delivery Options */}
        <div className="space-y-4 bg-gray-50 rounded-xl p-4">
          <h3 className="font-medium flex items-center gap-2">
            <Truck className="w-5 h-5 text-minted-600" />
            Delivery Options
          </h3>
          
          {/* Local Pickup */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium">Local Pickup</p>
                <p className="text-sm text-gray-500">Buyer collects from you</p>
              </div>
            </div>
            <Switch
              checked={formData.pickupAvailable}
              onCheckedChange={(checked) => setFormData({ ...formData, pickupAvailable: checked })}
            />
          </div>

          {/* Shipping */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium">Shipping Available</p>
                <p className="text-sm text-gray-500">You will post the item</p>
              </div>
            </div>
            <Switch
              checked={formData.shippingAvailable}
              onCheckedChange={(checked) => setFormData({ ...formData, shippingAvailable: checked })}
            />
          </div>

          {/* Weight Class Selection */}
          {formData.shippingAvailable && (
            <div className="pt-2">
              <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                <Scale className="w-4 h-4 text-minted-600" />
                Shipping Weight/Size
              </Label>
              <Select
                value={formData.weightClass}
                onValueChange={(value) => setFormData({ ...formData, weightClass: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parcel size" />
                </SelectTrigger>
                <SelectContent>
                  {WEIGHT_CLASSES.map(wc => (
                    <SelectItem key={wc.id} value={wc.id}>
                      {wc.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedWeightClass && (
                <p className="text-sm text-gray-500 mt-2">
                  {selectedWeightClass.description} — {selectedWeightClass.kg}kg, {selectedWeightClass.length}×{selectedWeightClass.width}×{selectedWeightClass.height}cm
                </p>
              )}
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-minted-600 hover:bg-minted-700 h-12 text-base"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Listing'
          )}
        </Button>
      </form>
    </div>
  );
};

export default EditListingPage;
